import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Plus, 
  Calendar as CalendarIcon, 
  Clock, 
  Video,
  MapPin,
  Phone,
  User,
  CheckCircle,
  XCircle,
  ExternalLink,
  Building2,
  FileText,
  X,
  RefreshCw
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlots = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00"
];

export default function AppointmentsPage({ user, logout }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterStatus, setFilterStatus] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [clinicDetails, setClinicDetails] = useState(null);
  const [newAppointment, setNewAppointment] = useState({
    customer_name: "",
    customer_phone: "",
    customer_id: "guest",
    appointment_type: "virtual",
    date: "",
    time: "",
    notes: ""
  });

  useEffect(() => {
    fetchAppointments();
  }, [filterStatus, selectedDate]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAppointments();
    }, 30000);
    return () => clearInterval(interval);
  }, [filterStatus]);

  const fetchAppointments = async () => {
    try {
      let url = `${API}/appointments?limit=100`;
      if (filterStatus) url += `&status=${filterStatus}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setAppointments(response.data.appointments || []);
    } catch (error) {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    if (!newAppointment.customer_name || !newAppointment.customer_phone || !newAppointment.date || !newAppointment.time) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      await axios.post(`${API}/appointments`, newAppointment, { withCredentials: true });
      toast.success("Appointment created successfully");
      setShowAddModal(false);
      setNewAppointment({
        customer_name: "",
        customer_phone: "",
        customer_id: "guest",
        appointment_type: "virtual",
        date: "",
        time: "",
        notes: ""
      });
      fetchAppointments();
    } catch (error) {
      toast.error("Failed to create appointment");
    }
  };

  const handleUpdateStatus = async (appointmentId, newStatus) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}`, { status: newStatus }, { withCredentials: true });
      toast.success("Appointment updated");
      fetchAppointments();
      // Update selected appointment if it's the one being modified
      if (selectedAppointment && selectedAppointment.appointment_id === appointmentId) {
        setSelectedAppointment(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error("Failed to update appointment");
    }
  };

  const handleAppointmentClick = async (apt) => {
    setSelectedAppointment(apt);
    setShowDetailModal(true);
    setClinicDetails(null);
    
    // Fetch clinic details if clinic_id exists
    if (apt.clinic_id) {
      try {
        const response = await axios.get(`${API}/clinics/${apt.clinic_id}`, { withCredentials: true });
        setClinicDetails(response.data);
      } catch (error) {
        console.log("Could not fetch clinic details:", error);
      }
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedAppointment(null);
    setClinicDetails(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: "bg-blue-100 text-blue-800",
      confirmed: "bg-green-100 text-green-800",
      completed: "bg-slate-100 text-slate-800",
      cancelled: "bg-red-100 text-red-800",
      no_show: "bg-orange-100 text-orange-800"
    };
    return colors[status] || "bg-slate-100 text-slate-800";
  };

  const getTypeIcon = (type) => {
    return type === "virtual" ? (
      <Video className="w-4 h-4 text-purple-500" />
    ) : (
      <MapPin className="w-4 h-4 text-cyan-500" />
    );
  };

  const todayAppointments = appointments.filter(
    (apt) => apt.date === format(selectedDate, "yyyy-MM-dd")
  );

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="appointments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Appointments</h1>
            <p className="text-slate-600 mt-1">Manage virtual clinic and in-person consultations</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => { fetchAppointments(); toast.success("Refreshed!"); }}
              data-testid="refresh-appointments-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
            <DialogTrigger asChild>
              <Button className="bg-cyan-700 hover:bg-cyan-800" data-testid="add-appointment-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Appointment
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Schedule Appointment</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateAppointment} className="space-y-4">
                <div>
                  <label className="form-label">Customer Name *</label>
                  <Input
                    placeholder="Full name"
                    value={newAppointment.customer_name}
                    onChange={(e) => setNewAppointment({ ...newAppointment, customer_name: e.target.value })}
                    data-testid="apt-name-input"
                  />
                </div>
                <div>
                  <label className="form-label">Phone *</label>
                  <Input
                    placeholder="Phone number"
                    value={newAppointment.customer_phone}
                    onChange={(e) => setNewAppointment({ ...newAppointment, customer_phone: e.target.value })}
                    data-testid="apt-phone-input"
                  />
                </div>
                <div>
                  <label className="form-label">Type</label>
                  <Select 
                    value={newAppointment.appointment_type} 
                    onValueChange={(value) => setNewAppointment({ ...newAppointment, appointment_type: value })}
                  >
                    <SelectTrigger data-testid="apt-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="virtual">Virtual Consultation</SelectItem>
                      <SelectItem value="clinic">Clinic Visit</SelectItem>
                      <SelectItem value="home">Home Visit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Date *</label>
                    <Input
                      type="date"
                      value={newAppointment.date}
                      onChange={(e) => setNewAppointment({ ...newAppointment, date: e.target.value })}
                      data-testid="apt-date-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">Time *</label>
                    <Select 
                      value={newAppointment.time} 
                      onValueChange={(value) => setNewAppointment({ ...newAppointment, time: value })}
                    >
                      <SelectTrigger data-testid="apt-time-select">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((slot) => (
                          <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="form-label">Notes</label>
                  <Textarea
                    placeholder="Any special requirements..."
                    value={newAppointment.notes}
                    onChange={(e) => setNewAppointment({ ...newAppointment, notes: e.target.value })}
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-cyan-700 hover:bg-cyan-800" data-testid="create-apt-btn">
                    Schedule
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-[Manrope]">Calendar</CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                className="rounded-md border"
              />
              <div className="mt-4">
                <Select value={filterStatus || "all"} onValueChange={(val) => setFilterStatus(val === "all" ? "" : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Appointments List */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-[Manrope]">
                Appointments for {format(selectedDate, "MMMM d, yyyy")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : todayAppointments.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No appointments scheduled for this date</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {todayAppointments.map((apt) => (
                    <div 
                      key={apt.appointment_id} 
                      className="p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                      data-testid={`appointment-${apt.appointment_id}`}
                      onClick={() => handleAppointmentClick(apt)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                            <User className="w-6 h-6 text-cyan-700" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">{apt.customer_name}</h4>
                            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                              <Phone className="w-3 h-3" />
                              {apt.customer_phone}
                            </p>
                            <div className="flex items-center gap-3 mt-2">
                              <span className="text-sm text-slate-600 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {apt.time}
                              </span>
                              <span className="text-sm text-slate-600 flex items-center gap-1">
                                {getTypeIcon(apt.appointment_type)}
                                {apt.appointment_type}
                              </span>
                            </div>
                            {apt.clinic_name && (
                              <p className="text-sm text-cyan-600 mt-1 flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {apt.clinic_name}
                              </p>
                            )}
                            {apt.appointment_type === "virtual" && (
                              <a 
                                href="https://meet.google.com/qkx-jgtp-nsp" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex mt-2"
                                data-testid={`join-meeting-${apt.appointment_id}`}
                              >
                                <Button size="sm" className="bg-purple-600 hover:bg-purple-700 h-7 text-xs">
                                  <Video className="w-3 h-3 mr-1" />
                                  Join Meet
                                </Button>
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2" onClick={(e) => e.stopPropagation()}>
                          <Badge className={getStatusColor(apt.status)}>
                            {apt.status}
                          </Badge>
                          {apt.status === "scheduled" && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 hover:bg-green-50"
                                onClick={() => handleUpdateStatus(apt.appointment_id, "confirmed")}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleUpdateStatus(apt.appointment_id, "cancelled")}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          {apt.status === "confirmed" && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleUpdateStatus(apt.appointment_id, "completed")}
                            >
                              Mark Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Appointments */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-[Manrope]">All Appointments</CardTitle>
          </CardHeader>
          <CardContent>
            {appointments.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <p>No appointments found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Date & Time</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointments.map((apt) => (
                      <tr 
                        key={apt.appointment_id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleAppointmentClick(apt)}
                        data-testid={`appointment-row-${apt.appointment_id}`}
                      >
                        <td>
                          <div>
                            <p className="font-medium">{apt.customer_name}</p>
                            <p className="text-sm text-slate-500">{apt.customer_phone}</p>
                          </div>
                        </td>
                        <td>
                          <div>
                            <p>{apt.date}</p>
                            <p className="text-sm text-slate-500">{apt.time}</p>
                          </div>
                        </td>
                        <td>
                          <span className="flex items-center gap-2">
                            {getTypeIcon(apt.appointment_type)}
                            {apt.appointment_type}
                          </span>
                        </td>
                        <td>
                          <Badge className={getStatusColor(apt.status)}>
                            {apt.status}
                          </Badge>
                        </td>
                        <td>
                          <p className="text-sm text-slate-500 truncate max-w-[200px]">
                            {apt.clinic_name || apt.notes || "-"}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Appointment Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-cyan-700" />
                Appointment Details
              </DialogTitle>
              <DialogDescription className="sr-only">
                View complete appointment information including customer, date, time, and clinic details.
              </DialogDescription>
            </DialogHeader>
            {selectedAppointment && (
              <div className="space-y-6" data-testid="appointment-detail-modal">
                {/* Customer Info */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Customer Information</h4>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-cyan-700" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{selectedAppointment.customer_name}</p>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {selectedAppointment.customer_phone}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Appointment Info */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Appointment Info</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Date</p>
                      <p className="font-medium flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-cyan-600" />
                        {selectedAppointment.date}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Time</p>
                      <p className="font-medium flex items-center gap-2">
                        <Clock className="w-4 h-4 text-cyan-600" />
                        {selectedAppointment.time}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Type</p>
                      <p className="font-medium flex items-center gap-2">
                        {getTypeIcon(selectedAppointment.appointment_type)}
                        <span className="capitalize">{selectedAppointment.appointment_type}</span>
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Status</p>
                      <Badge className={getStatusColor(selectedAppointment.status)}>
                        {selectedAppointment.status}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Virtual Meeting Link - Only for virtual appointments */}
                {selectedAppointment.appointment_type === "virtual" && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Virtual Consultation</h4>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                              <Video className="w-5 h-5 text-purple-700" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">Google Meet</p>
                              <p className="text-sm text-slate-500">Video consultation room</p>
                            </div>
                          </div>
                          <a 
                            href="https://meet.google.com/qkx-jgtp-nsp" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex"
                            data-testid="join-meeting-btn"
                          >
                            <Button className="bg-purple-600 hover:bg-purple-700">
                              <Video className="w-4 h-4 mr-2" />
                              Join Meeting
                            </Button>
                          </a>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Clinic Info */}
                {(selectedAppointment.clinic_name || clinicDetails) && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Clinic Information</h4>
                      <div className="bg-cyan-50 rounded-lg p-4 space-y-2">
                        <div className="flex items-start gap-3">
                          <Building2 className="w-5 h-5 text-cyan-700 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">
                              {clinicDetails?.name || selectedAppointment.clinic_name}
                            </p>
                            {(clinicDetails?.city || selectedAppointment.city) && (
                              <p className="text-sm text-slate-600">
                                {clinicDetails?.city || selectedAppointment.city}
                              </p>
                            )}
                            {clinicDetails?.address && (
                              <p className="text-sm text-slate-500 mt-1 flex items-start gap-1">
                                <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {clinicDetails.address}
                              </p>
                            )}
                            {clinicDetails?.phone && (
                              <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                                <Phone className="w-3 h-3" />
                                {clinicDetails.phone}
                              </p>
                            )}
                            {clinicDetails?.google_maps_url && (
                              <a 
                                href={clinicDetails.google_maps_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1 mt-2"
                              >
                                <ExternalLink className="w-3 h-3" />
                                Open in Google Maps
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Notes */}
                {selectedAppointment.notes && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Notes</h4>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600 flex items-start gap-2">
                          <FileText className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                          {selectedAppointment.notes}
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  {selectedAppointment.status === "scheduled" && (
                    <>
                      <Button
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          handleUpdateStatus(selectedAppointment.appointment_id, "confirmed");
                        }}
                        data-testid="detail-confirm-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Confirm
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => {
                          handleUpdateStatus(selectedAppointment.appointment_id, "cancelled");
                        }}
                        data-testid="detail-cancel-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    </>
                  )}
                  {selectedAppointment.status === "confirmed" && (
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        handleUpdateStatus(selectedAppointment.appointment_id, "completed");
                      }}
                      data-testid="detail-complete-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark as Completed
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    onClick={closeDetailModal}
                    data-testid="detail-close-btn"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
